import NativeWordHistory from './NativeWordHistory';
import ManagementFsAuthorization from './ManagementFsAuthorization';
import FinalRelease from './FinalRelease';
import DisclosureAssessment from "./DisclosureAssessment";
import FinancialStatementBuilder from "./FinancialStatementBuilder";
import OtherInformationAssessment from "./OtherInformationAssessment";
import AuditorReportDraft from "./AuditorReportDraft";
import ReportingJudgmentAssessment from "./ReportingJudgmentAssessment";
import { exactMoney } from "@/lib/exactMoney";
import { useState } from "react";
import { useReportingResource } from "@/hooks/useReportingResource";
import { downloadFile } from "@/lib/api";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Progress } from "@/components/ui/progress";
import { RefreshCw, Download, AlertTriangle, Plus, CheckCircle2 } from "lucide-react";

const fmt = (n) => exactMoney(n).replace(/^Rp /, "");

export default function Reporting({ id }) {
  return (
    <Tabs defaultValue="dash">
      <TabsList className="mb-3 flex-wrap h-auto">
        <TabsTrigger value="dash" data-testid="rep-tab-dash">Dashboard & Rilis</TabsTrigger>
        <TabsTrigger value="judgment" data-testid="rep-tab-judgment">Pertimbangan Pelaporan</TabsTrigger>
        <TabsTrigger value="draft" data-testid="rep-tab-draft">Draf Laporan Auditor</TabsTrigger>
        <TabsTrigger value="other" data-testid="rep-tab-other">Informasi Lain</TabsTrigger>
        <TabsTrigger value="ftb" data-testid="rep-tab-ftb">Final TB</TabsTrigger>
        <TabsTrigger value="builder" data-testid="rep-tab-builder">Input Laporan</TabsTrigger>
        <TabsTrigger value="fs" data-testid="rep-tab-fs">Laporan Keuangan</TabsTrigger>
        <TabsTrigger value="disclosure" data-testid="rep-tab-disclosure">Reviu Pengungkapan</TabsTrigger>
        <TabsTrigger value="calk" data-testid="rep-tab-calk">CALK</TabsTrigger>
        <TabsTrigger value="trans" data-testid="rep-tab-trans">Reviu Dwibahasa</TabsTrigger>
      </TabsList>
      <TabsContent value="dash"><Dash key={id} id={id} /></TabsContent>
      <TabsContent value="judgment"><ReportingJudgmentAssessment id={id} /></TabsContent>
      <TabsContent value="draft"><AuditorReportDraft id={id} /></TabsContent>
      <TabsContent value="other"><OtherInformationAssessment id={id} /></TabsContent>
      <TabsContent value="ftb"><div className="space-y-3"><TbBalanceBasis key={`basis-${id}`} id={id} /><FinalTB key={id} id={id} /></div></TabsContent>
      <TabsContent value="builder"><FinancialStatementBuilder key={id} id={id} /></TabsContent>
      <TabsContent value="fs"><FS key={id} id={id} /></TabsContent>
      <TabsContent value="disclosure"><DisclosureAssessment id={id} /></TabsContent>
      <TabsContent value="calk"><CALK key={id} id={id} /></TabsContent>
      <TabsContent value="trans"><Trans key={id} id={id} /></TabsContent>
    </Tabs>
  );
}

const Score = ({ label, v }) => (
  <div className="bg-white border border-border rounded-sm p-2">
    <div className={`text-lg font-head font-bold ${v >= 100 ? "text-green-700" : v >= 60 ? "text-amber-700" : "text-red-700"}`}>{v}%</div>
    <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
    <Progress value={v} className="h-1 mt-1" />
  </div>
);

export function Dash({ id }) {
  const { data: d, mutate, busy, error } = useReportingResource(id, "reporting/dashboard");
  if (!d) return <div role={error ? "alert" : undefined}>{error || "Memuat…"}</div>;
  const approve = () => mutate("post", `/engagements/${id}/reporting/approve`, undefined, r => `Dataset disetujui v${r.data.version}`);
  return (
    <div className="space-y-3" data-testid="reporting-dashboard">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        <Score label="Kelengkapan FS" v={d.fs.score} />
        <Score label="Kelengkapan CALK" v={d.calk.score} />
        <Score label="Kelengkapan Terjemahan" v={d.translation.score} />
        <div className="bg-white border border-border rounded-sm p-2">
          <div className="text-[11px]">Final TB v{d.final_tb.version} {d.final_tb.stale && <span className="text-red-600">STALE</span>}</div>
          <div className="text-[11px]">Dataset Disetujui v{d.approved.version} {d.approved.stale && <span className="text-red-600">STALE</span>}</div>
        </div>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
        <div className={`border rounded-sm p-2 ${d.fs.balanced ? "bg-green-50 border-green-300" : "bg-red-50 border-red-300"}`}>Neraca: {d.fs.balanced ? "SEIMBANG" : `TIDAK SEIMBANG (${fmt(d.fs.bs_difference)})`}</div>
        <div className="bg-white border border-border rounded-sm p-2">Aset: {fmt(d.fs.assets)}</div>
        <div className="bg-white border border-border rounded-sm p-2">Liabilitas + Ekuitas: {fmt(d.fs.liabilities_and_equity)}</div>
        <div className="bg-white border border-border rounded-sm p-2">Laba Bersih: {fmt(d.fs.net_income)}</div>
      </div>

      <div className={`border rounded-sm p-3 ${d.gate_reasons.length ? "bg-red-50 border-red-300" : "bg-green-50 border-green-300"}`} data-testid="rep-gate">
        <div className="font-head font-semibold text-sm flex items-center gap-1 mb-1">
          {d.gate_reasons.length ? <><AlertTriangle size={14} className="text-red-600" />Gerbang Rilis Laporan: DIBLOKIR ({d.gate_reasons.length})</> : <><CheckCircle2 size={14} className="text-green-600" />Gerbang Rilis Laporan: LOLOS</>}
        </div>
        {d.gate_reasons.map((r, i) => <div key={i} className="text-[11px] text-red-700" data-testid={`rep-gate-${i}`}>• {r}</div>)}
      </div>

      <div className="flex flex-wrap gap-2">
        <Button size="sm" disabled={busy} onClick={approve} data-testid="rep-approve"><CheckCircle2 size={13} className="mr-1" />Setujui Dataset Pelaporan</Button>
        <Button size="sm" variant="outline" onClick={() => downloadFile(`/engagements/${id}/export/fs-word`, "Laporan_Keuangan.docx")} data-testid="rep-fs-word"><Download size={13} className="mr-1" />Word Laporan Keuangan</Button>
        <Button size="sm" variant="outline" onClick={() => downloadFile(`/engagements/${id}/export/auditor-report`, "Laporan_Auditor.docx")} data-testid="rep-auditor"><Download size={13} className="mr-1" />Word Laporan Auditor</Button>
      </div>
      <section aria-label="Riwayat laporan keuangan"><h3 className="font-semibold text-sm">Riwayat laporan keuangan</h3>
        <NativeWordHistory id={id} exportType="D110" recordId={`financial-statements:${id}`}/></section>
      <ManagementFsAuthorization id={id}/>
      <FinalRelease id={id} exportType="D110"/>
      <section aria-label="Riwayat draf awal laporan auditor"><h3 className="font-semibold text-sm">Riwayat draf awal laporan auditor</h3>
        <NativeWordHistory id={id} exportType="D140_LEGACY" recordId={`legacy-auditor-report:${id}`}/></section>
    </div>
  );
}

// B-459: a single balance column does not say how its sign is carried; the auditor declares it from the TB source.
const BASIS_LABELS = { debit_positive: "Debit positif — saldo kredit tercatat negatif", normal_balance: "Searah saldo normal — semua saldo positif" };
export function TbBalanceBasis({ id, onDeclared }) {
  const { data: d, mutate, busy, error } = useReportingResource(id, "reporting/tb-balance-basis");
  const [basis, setBasis] = useState("");
  const [reason, setReason] = useState("");
  if (!d) return error ? <div role="alert">{error}</div> : null;
  const save = async () => { if (await mutate("put", `/engagements/${id}/reporting/tb-balance-basis`, { basis, reason }, "Konvensi tanda TB dicatat")) { setReason(""); onDeclared && onDeclared(); } };
  return (
    <div className="bg-white border border-border rounded-sm p-3 space-y-2 text-xs" data-testid="tb-basis">
      <div className="font-head font-semibold text-sm">Konvensi tanda saldo TB untuk laporan keuangan</div>
      <p className="text-muted-foreground">{d.basis}</p>
      <p data-testid="tb-basis-facts">{d.single_column_accounts} akun bersaldo satu kolom · {d.credit_normal_stored_negative} dari {d.credit_normal_accounts} akun bersaldo normal kredit tersimpan negatif.</p>
      <p data-testid="tb-basis-declared">{d.declared ? `Dinyatakan: ${BASIS_LABELS[d.declared.basis]} · ${d.declared.declared_by_name} (${d.declared.declared_by_role}) · ${String(d.declared.declared_at).slice(0, 10)} · ${d.declared.reason}` : "Belum dinyatakan — saldo satu kolom dibaca searah saldo normal."}</p>
      <div className="flex flex-wrap items-end gap-2">
        <select aria-label="Konvensi tanda saldo TB" className="border p-2" value={basis} onChange={(e) => setBasis(e.target.value)} data-testid="tb-basis-select">
          <option value="">Pilih konvensi dari sumber TB</option>{(d.options || []).map((o) => <option key={o} value={o}>{BASIS_LABELS[o] || o}</option>)}
        </select>
        <Input className="h-9 w-96 max-w-full" placeholder="Dasar dari sumber TB (wajib)" value={reason} onChange={(e) => setReason(e.target.value)} data-testid="tb-basis-reason" />
        <Button size="sm" disabled={busy || !basis || !reason.trim()} onClick={save} data-testid="tb-basis-save">Nyatakan konvensi</Button>
      </div>
    </div>
  );
}

// The engagement's TB population (tb_population) decides what this table is: only CANONICAL is the engagement's Final TB.
// Otherwise the figures stay visible as labelled working figures, with every backend reason and the recovery path;
// approval, final-authority exports and readiness stay closed in the backend whatever this screen shows.
const POPULATION_LABEL = {
  CANONICAL: "CANONICAL — TB lengkap yang dinyatakan dan valid",
  UNDECLARED: "NON-CANONICAL — populasi TB belum dinyatakan lengkap: angka kerja, bukan Final TB canonical",
  NOT_CANONICAL: "NON-CANONICAL — populasi TB tidak valid: Final TB baru tidak dibuat",
};

export function FinalTbPopulation({ population, generated, snapshotted }) {
  if (!population) return null;
  const canonical = population.state === "CANONICAL";
  const snapshotWorking = snapshotted && generated && generated.state !== "CANONICAL";
  return (
    <div className={`border rounded-sm p-2 text-xs space-y-1 ${canonical ? "bg-green-50 border-green-300" : "bg-red-50 border-red-300"}`}
         role={canonical ? undefined : "alert"} data-testid="ftb-population">
      <div className={`font-semibold ${canonical ? "text-green-800" : "text-red-800"}`} data-testid="ftb-population-state">
        {POPULATION_LABEL[population.state] || population.state}</div>
      {population.dataset && <div data-testid="ftb-population-dataset">Sumber: {population.dataset.label} · {population.dataset.entity} per {population.dataset.period_end}</div>}
      {population.reasons?.length > 0 && <ul className="list-disc pl-5" data-testid="ftb-population-reasons">{population.reasons.map((r) => <li key={r}>{r}</li>)}</ul>}
      {!canonical && <div data-testid="ftb-population-recovery">
        Pemulihan: Data Intake → dataset TB → "Populasi TB perikatan": perbaiki sumber / pemetaan / rekonsiliasi, nyatakan TB lengkap
        (FULL_TB) untuk entitas dan periode perikatan, bangun ulang akun, lalu "Buat / Segarkan Final TB". Persetujuan pelaporan,
        ekspor berotoritas final dan readiness tetap tertutup sampai CANONICAL.</div>}
      {canonical && snapshotWorking && <div className="text-amber-800" data-testid="ftb-population-regenerate">
        Final TB tersimpan dibuat saat populasi {generated.state}; buat ulang Final TB agar tersimpan sebagai Final TB canonical.</div>}
    </div>
  );
}

export function FinalTB({ id }) {
  const { data: d, mutate, busy, error } = useReportingResource(id, "final-tb");
  if (!d) return error ? <div role="alert">{error}</div> : null;
  const gen = () => mutate("post", `/engagements/${id}/final-tb/generate`, undefined,
    (response) => (response?.data?.population?.state === "CANONICAL" ? "Final TB canonical dibuat"
      : `Final TB kerja dibuat — NON-CANONICAL (${response?.data?.population?.state || "status populasi tidak diketahui"})`));
  const canonical = d.population?.state === "CANONICAL" && (!d.snapshotted || d.generated_population?.state === "CANONICAL");
  return (
    <div className="space-y-2" data-testid="final-tb">
      <FinalTbPopulation population={d.population} generated={d.generated_population} snapshotted={d.snapshotted} />
      <div className="flex items-center gap-2">
        <span className="text-sm font-head font-semibold" data-testid="ftb-title">{canonical ? `Final TB v${d.version}` : `Final TB kerja (NON-CANONICAL) v${d.version}`}</span>
        {d.stale && <span className="text-[10px] px-2 py-0.5 border rounded-sm bg-red-100 text-red-700 border-red-300" data-testid="ftb-stale">STALE — TB/AJE berubah</span>}
        <Button size="sm" disabled={busy} onClick={gen} data-testid="ftb-generate"><RefreshCw size={13} className="mr-1" />Buat / Segarkan Final TB</Button>
      </div>
      <div className="bg-white border border-border rounded-sm overflow-x-auto"><table className="w-full audit-table">
        <thead><tr><th>Kode</th><th>Akun</th><th>FS Line</th><th className="text-right">Raw TB</th><th className="text-right">Penyesuaian (AJE)</th><th className="text-right">Final TB</th><th className="text-right">Tahun Lalu</th></tr></thead>
        <tbody>
          {d.lines.map((l) => (
            <tr key={l.account_code}><td className="font-mono text-xs">{l.account_code}</td><td className="text-xs">{l.account_name}</td><td className="text-xs">{l.fs_line}</td>
              <td className="text-right tabular">{fmt(l.raw)}</td><td className="text-right tabular">{fmt(l.adjustment)}</td><td className="text-right tabular font-medium">{fmt(l.final)}</td><td className="text-right tabular text-muted-foreground">{fmt(l.prior)}</td></tr>
          ))}
          <tr className="font-semibold"><td colSpan={3}>TOTAL</td><td className="text-right tabular">{fmt(d.totals.raw)}</td><td className="text-right tabular">{fmt(d.totals.adjustment)}</td><td className="text-right tabular">{fmt(d.totals.final)}</td><td></td></tr>
        </tbody>
      </table></div>
    </div>
  );
}

const SecTable = ({ title, sec }) => (
  <div className="bg-white border border-border rounded-sm">
    <div className="px-3 py-1.5 border-b border-border font-head font-semibold text-xs bg-muted/40">{title}</div>
    <table className="w-full audit-table"><tbody>
      {sec.rows.map((r) => <tr key={r.fs_line}><td className="text-xs">{r.fs_line} <span className="text-muted-foreground">/ {r.en}</span></td><td className="text-right tabular">{fmt(r.cy)}</td><td className="text-right tabular text-muted-foreground">{fmt(r.py)}</td></tr>)}
      <tr className="font-semibold"><td>Subtotal</td><td className="text-right tabular">{fmt(sec.cy)}</td><td className="text-right tabular">{fmt(sec.py)}</td></tr>
    </tbody></table>
  </div>
);

export function FS({ id }) {
  const { data: d, error } = useReportingResource(id, "financial-statements");
  if (!d) return error ? <div role="alert">{error}</div> : null;
  return (
    <div className="space-y-3" data-testid="fs-view">
      <div className={`border rounded-sm p-2 text-sm font-medium ${d.balanced ? "bg-green-50 border-green-300 text-green-700" : "bg-red-50 border-red-300 text-red-700"}`} data-testid="fs-balance">
        Neraca {d.balanced ? "SEIMBANG" : `TIDAK SEIMBANG — Selisih ${fmt(d.bs_difference)}`} · Aset {fmt(d.assets.cy)} vs Liabilitas+Ekuitas {fmt(d.liabilities_and_equity)}
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <div className="space-y-2"><div className="text-xs font-head font-semibold uppercase text-muted-foreground">Laporan Posisi Keuangan</div>
          <SecTable title="Aset / Assets" sec={d.assets} /><SecTable title="Liabilitas / Liabilities" sec={d.liabilities} /><SecTable title="Ekuitas / Equity" sec={d.equity} /></div>
        <div className="space-y-2"><div className="text-xs font-head font-semibold uppercase text-muted-foreground">Laporan Laba Rugi</div>
          <SecTable title="Pendapatan / Revenue" sec={d.revenue} /><SecTable title="Beban / Expenses" sec={d.expenses} />
          <div className="bg-white border border-border rounded-sm px-3 py-1.5 text-sm font-semibold">Laba Bersih / Net Income: {fmt(d.net_income)}</div>
          <div className="border p-2 text-sm">OCI: {d.oci_total == null ? "Belum tersedia" : fmt(d.oci_total)} · Total penghasilan komprehensif: {d.total_comprehensive_income == null ? "Belum tersedia" : fmt(d.total_comprehensive_income)}</div>
          <div className="bg-white border border-border rounded-sm px-3 py-1.5 text-[11px]">Tie-out Arus Kas: {d.cf_tieout_ok === null ? "—" : d.cf_tieout_ok ? "OK" : "GAGAL"} · Tie-out Ekuitas: {d.eq_tieout_ok === null ? "—" : d.eq_tieout_ok ? "OK" : "GAGAL"}</div>
        </div>
      </div>
    </div>
  );
}

export function CALK({ id }) {
  const { data, mutate, busy, error } = useReportingResource(id, "calk");
  const rows = data || [];
  const add = () => { const t = window.prompt("Judul catatan (ID):"); if (t) return mutate("post", `/engagements/${id}/calk`, { title_id: t }); };
  const upd = (n, patch) => mutate("put", `/calk/${n.id}`, patch);
  return (
    <div className="space-y-2" data-testid="calk-view">
      <Button size="sm" disabled={busy} onClick={add} data-testid="calk-add"><Plus size={13} className="mr-1" />Tambah Catatan</Button>
      {rows.map((n) => (
        <div key={n.id} className="bg-white border border-border rounded-sm p-2 space-y-1" data-testid={`calk-row-${n.id}`}>
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs font-semibold">Catatan {n.note_no}</span>
            <span className="text-[10px]">Kelengkapan {n.completeness.score}%{n.completeness.exceptions_count > 0 && <span className="text-red-600"> · {n.completeness.exceptions_count} eksepsi</span>}</span>
            {n.tieout_ok === false && <span className="text-[10px] px-1.5 py-0.5 border rounded-sm bg-red-100 text-red-700 border-red-300">TIE-OUT GAGAL — PERLU REVIU</span>}
            {n.tieout_ok === true && <span className="text-[10px] px-1.5 py-0.5 border rounded-sm bg-green-100 text-green-700 border-green-300">TIE-OUT OK</span>}
          </div>
          <div className="grid grid-cols-2 gap-2">
            <Input disabled={busy} className="h-7 text-xs" placeholder="Judul (ID)" defaultValue={n.title_id} onBlur={(e) => e.target.value !== n.title_id && upd(n, { title_id: e.target.value })} data-testid={`calk-title-id-${n.id}`} />
            <Input disabled={busy} className="h-7 text-xs" placeholder="Judul (EN)" defaultValue={n.title_en} onBlur={(e) => e.target.value !== n.title_en && upd(n, { title_en: e.target.value })} data-testid={`calk-title-en-${n.id}`} />
            <Input disabled={busy} className="h-7 text-xs" placeholder="FS Line terkait" defaultValue={n.fs_line} onBlur={(e) => e.target.value !== n.fs_line && upd(n, { fs_line: e.target.value })} />
            <Input disabled={busy} className="h-7 text-xs" type="text" inputMode="decimal" placeholder="Jumlah Catatan" defaultValue={n.note_total ?? ""} onBlur={(e) => upd(n, { note_total: e.target.value.trim() || null, note_total_en: e.target.value.trim() || null })} data-testid={`calk-total-${n.id}`} />
            <Textarea disabled={busy} rows={2} className="text-xs" placeholder="Isi (ID)" defaultValue={n.content_id} onBlur={(e) => e.target.value !== n.content_id && upd(n, { content_id: e.target.value })} />
            <Textarea disabled={busy} rows={2} className="text-xs" placeholder="Isi (EN)" defaultValue={n.content_en} onBlur={(e) => e.target.value !== n.content_en && upd(n, { content_en: e.target.value })} />
          </div>
          {n.fs_value != null && <div className="text-[10px] text-muted-foreground">Nilai FS Line: {fmt(n.fs_value)}</div>}
        </div>
      ))}
      {error && <div role="alert">{error}</div>}
      {!error && data && rows.length === 0 && <div className="text-xs text-muted-foreground">Belum ada catatan.</div>}
    </div>
  );
}

export function Trans({ id }) {
  const { data: d, error } = useReportingResource(id, "reporting/translation-review");
  if (!d) return error ? <div role="alert">{error}</div> : null;
  return (
    <div className="space-y-3" data-testid="translation-view">
      <div className="bg-white border border-border rounded-sm p-2 text-sm">Skor Kelengkapan Terjemahan: <b>{d.score}%</b></div>
      <div className="bg-white border border-border rounded-sm p-3">
        <div className="text-xs font-semibold mb-1">Selisih Angka ID vs EN ({d.numeric_mismatch.length})</div>
        {d.numeric_mismatch.length === 0 ? <div className="text-[11px] text-green-700">Tidak ada selisih.</div> :
          d.numeric_mismatch.map((m, i) => <div key={i} className="text-[11px] text-red-700" data-testid={`trans-mismatch-${i}`}>• {m.item}: ID {fmt(m.id)} ≠ EN {fmt(m.en)} — PERLU REVIU</div>)}
      </div>
      <div className="bg-white border border-border rounded-sm p-3">
        <div className="text-xs font-semibold mb-1">Terminologi EN Belum Lengkap ({d.missing_en.length})</div>
        {d.missing_en.length === 0 ? <div className="text-[11px] text-green-700">Semua istilah memiliki terjemahan.</div> :
          d.missing_en.map((m, i) => <div key={i} className="text-[11px] text-amber-700">• {m.item} ({m.where})</div>)}
      </div>
    </div>
  );
}
